import { HelpCircle, MessageCircle, Send, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AiIcon, Button } from '../../ui'
import { type Mensaje, useSupportChat } from './useSupportChat'

/**
 * El chat de ayuda del estudio.
 *
 * Un botón flotante y un panel. Vive en el layout, así que acompaña en todas las
 * pantallas: la duda llega mientras subes una boda, no en una sección de ayuda.
 *
 * El panel y no un diálogo modal a propósito: el estudio suele querer MIRAR la
 * pantalla mientras pregunta —«¿dónde está eso?»— y un modal la tapa entera.
 */

/**
 * Cómo firma cada burbuja. El asistente tiene nombre, pero SIEMPRE con el icono
 * «AI» de la UE y «asistente de IA» al lado: las personas firman con su nombre de
 * pila, un «Chasqui» suelto junto a «Rosa» parecería otra compañera, y
 * «asistente» a secas no dice que contesta una inteligencia artificial. Es lo que
 * pide el art. 50 del Reglamento de IA europeo al chat de Facturamigo, y la casa
 * hace lo mismo aquí: el DS 115-2025-PCM (art. 25) lo propone como etiquetado
 * visible. Las fuentes, en support.dataanalisis.com, `docs/transparencia-ia.md`.
 */
const ASISTENTE = 'Chasqui'
/** La firma de una persona del equipo. La del asistente es `FirmaDelAsistente`, con su icono. */
function firma(m: Mensaje): string | null {
  if (m.rol === 'human') return `${m.autor ?? 'Equipo'} · ChasquiTambo`
  return null
}

/** El icono va en la tinta del texto, no en la de la firma: tiene que verse. */
function FirmaDelAsistente() {
  return (
    <span className="mb-0.5 flex items-center gap-1.5 text-caption font-medium text-ink-muted">
      <AiIcon className="size-5 shrink-0 text-ink" />
      {ASISTENTE} · asistente de IA
    </span>
  )
}

/**
 * Lo que lee una máquina en cada respuesta del asistente, y solo en esas (la
 * bienvenida la escribimos nosotros): el atributo `ai-disclosure` que propone el
 * W3C y los microdatos de schema.org con el término `trainedAlgorithmicMedia` de
 * IPTC. El `<link>` con `itemProp` React no lo sube al `<head>`.
 */
const MARCA_DE_IA = {
  'ai-disclosure': 'ai-generated',
  itemScope: true,
  itemType: 'https://schema.org/Message',
} as const
const ORIGEN_IA = 'https://schema.org/TrainedAlgorithmicMediaDigitalSource'

/** `**así**` en negrita, que es todo el marcado que usa el asistente. */
function Texto({ valor }: { valor: string }) {
  return (
    <>
      {/* Sin el salto de línea del final: el asistente lo deja antes de su marca de
          pasar con una persona, y con `pre-wrap` se pinta como un hueco vacío. */}
      {valor.trimEnd().split(/(\*\*[^*]+\*\*)/g).map((parte, i) =>
        parte.startsWith('**') && parte.endsWith('**') ? (
          <strong key={i} className="font-medium">
            {parte.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{parte}</span>
        ),
      )}
    </>
  )
}

export function SupportChat() {
  const [abierto, setAbierto] = useState(false)
  const [texto, setTexto] = useState('')
  const chat = useSupportChat(abierto)
  const listaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    listaRef.current?.scrollTo({ top: listaRef.current.scrollHeight })
  }, [chat.mensajes, abierto])

  useEffect(() => {
    if (!abierto) return
    const cerrar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false)
    }
    window.addEventListener('keydown', cerrar)
    return () => window.removeEventListener('keydown', cerrar)
  }, [abierto])

  const enlaceWhatsapp =
    chat.whatsapp !== null && chat.referencia !== null
      ? `https://wa.me/${chat.whatsapp}?text=${encodeURIComponent(`Hola, vengo del chat de ayuda (${chat.referencia}).`)}`
      : null

  return (
    <>
      <button
        type="button"
        aria-label="Ayuda"
        onClick={() => setAbierto((v) => !v)}
        className="fixed bottom-20 right-4 z-40 flex size-12 items-center justify-center rounded-full bg-accent text-accent-contrast shadow-lg transition hover:brightness-110 md:bottom-6"
      >
        {abierto ? <X size={22} /> : <HelpCircle size={22} />}
      </button>

      {abierto && (
        <section
          aria-label="Chat de ayuda"
          className="fixed bottom-36 right-4 z-40 flex max-h-[70dvh] w-[min(24rem,calc(100vw-2rem))] flex-col rounded-2xl border border-line bg-surface-raised shadow-xl md:bottom-20"
        >
          <header className="flex items-center gap-2 border-b border-line px-4 py-3">
            <MessageCircle size={18} className="text-ink-muted" />
            <h2 className="text-body font-medium">Ayuda</h2>
          </header>

          <div ref={listaRef} className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
            {chat.mensajes.length === 0 && (
              /* Firmada como sus respuestas: la marca de IA se presenta antes de la primera pregunta. */
              <div className="rounded-xl bg-surface p-3 text-body-sm">
                <FirmaDelAsistente />
                <p className="text-ink-muted">
                  Hola, soy {ASISTENTE}, el asistente de inteligencia artificial (IA) de ChasquiTambo.
                  Pregunta lo que quieras: cómo compartir una entrega, qué ocupa tu plan, por qué se paró
                  una subida. Contesto con lo que dice la ayuda y puedo equivocarme.
                </p>
              </div>
            )}
            {chat.mensajes.map((m, i) => (
              <article
                key={m.seq ?? `m${i}`}
                {...(m.rol === 'assistant' ? MARCA_DE_IA : {})}
                className={
                  m.rol === 'user'
                    ? 'max-w-[85%] self-end whitespace-pre-wrap rounded-2xl bg-accent px-3 py-2 text-body-sm text-accent-contrast'
                    : 'max-w-[85%] self-start whitespace-pre-wrap rounded-2xl bg-surface px-3 py-2 text-body-sm'
                }
              >
                {m.rol === 'assistant' ? (
                  <>
                    <link itemProp="digitalSourceType" href={ORIGEN_IA} />
                    <FirmaDelAsistente />
                  </>
                ) : (
                  firma(m) !== null && (
                    <span className="mb-0.5 block text-caption font-medium text-accent">{firma(m)}</span>
                  )
                )}
                {m.pendiente && m.texto === '' ? (
                  <span className="text-ink-muted">…</span>
                ) : (
                  <span itemProp={m.rol === 'assistant' ? 'text' : undefined}>
                    <Texto valor={m.texto} />
                  </span>
                )}
              </article>
            ))}
          </div>

          {chat.estado === 'pending_human' && chat.escribiendo !== null && (
            <p className="mx-4 mb-2 text-body-sm text-ink-muted italic" aria-live="polite">
              {chat.escribiendo.name ?? 'Alguien del equipo'} está escribiendo…
            </p>
          )}
          {chat.estado === 'pending_human' && (
            <p className="mx-4 mb-3 rounded-xl bg-surface p-3 text-body-sm">
              {chat.equipoConectado === true
                ? 'Hay alguien del equipo conectado: te responde aquí mismo. '
                : chat.equipoConectado === false
                  ? 'Ahora no hay nadie del equipo conectado. Te respondemos aquí en cuanto alguien se conecte. '
                  : 'Te responde una persona aquí mismo. '}
              Si prefieres seguir por WhatsApp, di que vienes del chat y da la referencia{' '}
              <strong className="font-medium">{chat.referencia}</strong>.
              {enlaceWhatsapp !== null && (
                <a href={enlaceWhatsapp} target="_blank" rel="noopener noreferrer" className="mt-1 block font-medium underline">
                  Abrir WhatsApp
                </a>
              )}
            </p>
          )}
          {chat.error !== null && <p className="mx-4 mb-3 text-body-sm text-danger">{chat.error}</p>}

          <form
            className="flex items-end gap-2 border-t border-line p-3"
            onSubmit={(e) => {
              e.preventDefault()
              const pendiente = texto
              setTexto('')
              void chat.enviar(pendiente)
            }}
          >
            <input
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value)
                if (e.target.value.trim() !== '') chat.avisarEscribiendo()
              }}
              placeholder={chat.estado === 'pending_human' ? 'Escríbele a la persona…' : 'Escribe tu pregunta'}
              maxLength={1000}
              autoComplete="off"
              disabled={chat.enviando}
              className="min-w-0 flex-1 rounded-xl border border-line bg-surface px-3 py-2 text-body-sm outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
            {/* `busy` aquí es el TEXTO que sustituye al contenido mientras espera, no un booleano. */}
            <Button type="submit" size="sm" busy={chat.enviando && 'Enviando…'} disabled={texto.trim() === ''} aria-label="Enviar">
              <Send size={16} />
            </Button>
          </form>

          <div className="flex items-center justify-between px-4 pb-3">
            {chat.conversacion !== null && chat.estado === 'bot' ? (
              <button type="button" onClick={() => void chat.pedirPersona()} disabled={chat.enviando} className="text-caption text-ink-muted underline">
                Hablar con una persona
              </button>
            ) : (
              <span />
            )}
            {chat.conversacion !== null && (
              <button type="button" onClick={chat.reiniciar} className="text-caption text-ink-muted underline">
                Nueva consulta
              </button>
            )}
          </div>
        </section>
      )}
    </>
  )
}
